import React, { useState, useEffect } from 'react'
import type { LocationDetail } from '../../../../../types/location'
import { useLocationUpdate } from '../../../hooks/useLocationUpdate'
import { usePantryStore } from '../../../../../providers/PantryProvider'
import { GalleryPickerWithProvider } from '../../../../media/components/GalleryPickerWithProvider'
import type { PantryMediaFile } from '../../../../media'
import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS,
  getStateFieldLabel,
  normalizeCountry,
  normalizeState,
  type AddressOption,
} from '../../../constants/address'

interface Props {
  open: boolean
  location: LocationDetail
  onClose: () => void
}

interface FormValues {
  name: string
  license: string
  address1: string
  address2: string
  city: string
  state: string
  country: string
  postalCode: string
  phone: string
  email: string
  photoUrl: string
}

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  outline: 'none',
  color: '#111827',
  background: '#fff',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: '#9CA3AF',
  gridColumn: 'span 2',
  paddingTop: 4,
}

const FormField: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
  span?: number
  type?: string
  placeholder?: string
}> = ({ label, value, onChange, span = 1, type = 'text', placeholder }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#2563EB'
        e.currentTarget.style.boxShadow = '0 0 0 2px rgba(37,99,235,0.12)'
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = '#E5E7EB'
        e.currentTarget.style.boxShadow = 'none'
      }}
    />
  </div>
)

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
}

// Keep a legacy/free-text value selectable instead of silently dropping it
// when it does not match any known option.
const withCurrentValue = (
  options: AddressOption[],
  current: string,
): AddressOption[] =>
  current && !options.some((o) => o.value === current)
    ? [...options, { value: current, label: current }]
    : options

const SelectField: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
  options: AddressOption[]
  placeholder?: string
}> = ({ label, value, onChange, options, placeholder = 'Select…' }) => {
  const id = React.useId()
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={selectStyle}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#2563EB'
          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(37,99,235,0.12)'
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#E5E7EB'
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        <option value="">{placeholder}</option>
        {withCurrentValue(options, value).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

const getFolderId = (folderId: string) => folderId
const isFileImage = (file: unknown) => {
  const f = file as { content_type?: string }
  return !!(f?.content_type?.startsWith('image/'))
}

export const EditLocationDialog: React.FC<Props> = ({ open, location, onClose }) => {
  const store = usePantryStore()
  const orgId = store?.orgId ?? ''
  const { mutate: updateDetails, isPending } = useLocationUpdate()
  const [galleryOpen, setGalleryOpen] = useState(false)

  const [form, setForm] = useState<FormValues>({
    name: '',
    license: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    phone: '',
    email: '',
    photoUrl: '',
  })

  useEffect(() => {
    if (open) {
      const country = normalizeCountry(location.country)
      setForm({
        name: location.name ?? '',
        license: location.license ?? '',
        address1: location.address1 ?? '',
        address2: location.address2 ?? '',
        city: location.city ?? '',
        state: normalizeState(country, location.state),
        country,
        postalCode: location.postalCode ?? '',
        phone: formatPhone(location.phone ?? ''),
        email: location.email ?? '',
        photoUrl: location.picture ?? '',
      })
    }
  }, [open, location])

  const set = (key: keyof FormValues) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleCountryChange = (next: string) => {
    setForm((prev) => {
      const stillValid = (STATE_OPTIONS[next] ?? []).some(
        (o) => o.value === prev.state,
      )
      return { ...prev, country: next, state: stillValid ? prev.state : '' }
    })
  }

  const handleGallerySave = (files: PantryMediaFile[]) => {
    if (files.length > 0) {
      const file = files[0]
      set('photoUrl')(file.src || file.thumbnail || '')
    }
    setGalleryOpen(false)
  }

  const handleSave = () => {
    updateDetails(
      {
        warehouseId: location.id,
        data: {
          name: form.name,
          license: form.license,
          address_1: form.address1,
          address_2: form.address2,
          city: form.city,
          state: form.state,
          country: form.country,
          postal_code: form.postalCode,
          phone_number: form.phone.replace(/\D/g, ''),
          email: form.email,
          photo_url: form.photoUrl,
        },
      },
      { onSuccess: onClose },
    )
  }

  if (!open) return null

  return (
    <>
      {/* Dialog backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 1200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
        />

        <div
          style={{
            position: 'relative', background: '#fff', borderRadius: 14,
            width: 620, maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 48px)', display: 'flex',
            flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '18px 24px', borderBottom: '1px solid #E5E7EB',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
              Edit Location Details
            </h3>
            <button
              onClick={onClose} disabled={isPending}
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                color: '#9CA3AF', fontSize: 20, lineHeight: 1, padding: '2px 6px', borderRadius: 4,
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            {/* Store picture */}
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Store Picture</label>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                <div
                  onClick={() => setGalleryOpen(true)}
                  style={{
                    width: 96, height: 96, borderRadius: 10, overflow: 'hidden',
                    border: '2px dashed #D1D5DB', background: '#F9FAFB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0, position: 'relative',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.background = '#EFF6FF' }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.background = form.photoUrl ? 'transparent' : '#F9FAFB' }}
                >
                  {form.photoUrl ? (
                    <img src={form.photoUrl} alt={form.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#9CA3AF" strokeWidth="1.4">
                      <rect x="3" y="6" width="22" height="17" rx="3" />
                      <circle cx="10" cy="13" r="2.5" />
                      <path d="M3 19l5-5 4 4 4-3 6 5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setGalleryOpen(true)}
                    style={{
                      padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
                      color: '#374151', background: '#F9FAFB', border: '1px solid #E5E7EB',
                      borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <rect x="1" y="1" width="11" height="11" rx="2" />
                      <circle cx="5" cy="5" r="1.5" />
                      <path d="M1 9l3-3 3 3 2-2 3 3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {form.photoUrl ? 'Change image' : 'Select image'}
                  </button>
                  {form.photoUrl && (
                    <button
                      type="button"
                      onClick={() => set('photoUrl')('')}
                      style={{
                        marginTop: 6, padding: '5px 14px', fontSize: 12,
                        color: '#DC2626', background: 'none', border: 'none',
                        cursor: 'pointer', display: 'block',
                      }}
                    >
                      Remove
                    </button>
                  )}
                  <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '6px 0 0' }}>
                    Recommended: 400×400px or larger
                  </p>
                </div>
              </div>
            </div>

            {/* Fields grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
              <FormField label="Location Name *" value={form.name} onChange={set('name')} span={1} />
              <FormField label="License #" value={form.license} onChange={set('license')} span={1} placeholder="e.g. LIC-12345" />

              <div style={sectionLabelStyle}>Address</div>
              <FormField label="Street Address" value={form.address1} onChange={set('address1')} span={2} placeholder="123 Main St" />
              <FormField label="Apartment, suite, etc." value={form.address2} onChange={set('address2')} span={2} placeholder="Optional" />
              <FormField label="City" value={form.city} onChange={set('city')} />
              <SelectField
                label="Country"
                value={form.country}
                onChange={handleCountryChange}
                options={COUNTRY_OPTIONS}
              />
              <SelectField
                label={getStateFieldLabel(form.country)}
                value={form.state}
                onChange={set('state')}
                options={STATE_OPTIONS[form.country] ?? []}
              />
              <FormField label="Postal / Zip Code" value={form.postalCode} onChange={set('postalCode')} />

              <div style={sectionLabelStyle}>Contact</div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone')(formatPhone(e.target.value))}
                  placeholder="(555) 000-0000"
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(37,99,235,0.12)' }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none' }}
                />
              </div>
              <FormField label="Email" value={form.email} onChange={set('email')} type="email" placeholder="orders@example.com" />
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px', borderTop: '1px solid #E5E7EB',
            display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
          }}>
            <button
              onClick={onClose} disabled={isPending}
              style={{
                padding: '8px 18px', fontSize: 13.5, fontWeight: 500,
                borderRadius: 8, border: '1px solid #E5E7EB',
                background: '#fff', color: '#374151',
                cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave} disabled={isPending || !form.name.trim()}
              style={{
                padding: '8px 20px', fontSize: 13.5, fontWeight: 600,
                borderRadius: 8, border: 'none',
                background: isPending || !form.name.trim() ? '#93C5FD' : '#2563EB',
                color: '#fff',
                cursor: isPending || !form.name.trim() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {isPending && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ animation: 'spin 0.8s linear infinite' }}>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <path d="M7 1.5A5.5 5.5 0 1 1 1.5 7" strokeLinecap="round" />
                </svg>
              )}
              {isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Gallery picker - rendered outside dialog so it stacks on top */}
      <GalleryPickerWithProvider
        orgId={orgId}
        getFolderId={getFolderId}
        isFileImage={isFileImage}
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title="Select Store Picture"
        handleSave={handleGallerySave}
        multiple={false}
      />
    </>
  )
}
